import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Bot, Check, X, ExternalLink, Trash2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Eyebrow, Spinner } from "@/components/tac";

const UNDO_MS = 5000;

export default function Review() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDecide = ["owner", "dispatcher", "technician"].includes(user?.role);
  const canDelete = ["owner", "dispatcher"].includes(user?.role);
  const [pending, setPending] = useState(new Set());
  const timers = useRef({});
  const { data, isLoading } = useQuery({ queryKey: ["review"], queryFn: async () => (await api.get("/review")).data });
  const visible = (data || []).filter((d) => !pending.has(d.id));

  const refresh = () => { qc.invalidateQueries({ queryKey: ["review"] }); qc.invalidateQueries({ queryKey: ["overview"] }); };
  const hide = (ids) => setPending((p) => new Set([...p, ...ids]));
  const unhide = (ids) => setPending((p) => { const n = new Set(p); ids.forEach((i) => n.delete(i)); return n; });

  const decide = async (id, decision) => {
    try { await api.post(`/review/${id}`, { decision }); toast.success(decision === "approve" ? "Approved" : "Rejected"); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const scheduleDelete = (ids, commit, label) => {
    hide(ids);
    const key = ids.join(",");
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(async () => {
      delete timers.current[key];
      try { await commit(); refresh(); }
      catch (e) { unhide(ids); toast.error(apiErr(e.response?.data?.detail)); }
    }, UNDO_MS);
    toast(label, {
      duration: UNDO_MS,
      action: {
        label: "Undo",
        onClick: () => { clearTimeout(timers.current[key]); delete timers.current[key]; unhide(ids); toast.success("Restored"); },
      },
    });
  };

  const deleteOne = (d) => scheduleDelete([d.id], () => api.delete(`/review/${d.id}`), "AI draft deleted");
  const clearAll = () => {
    const ids = visible.map((d) => d.id);
    if (!ids.length) return;
    scheduleDelete(ids, () => api.post("/review/bulk-delete", { all: true }), `Cleared ${ids.length} draft(s)`);
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="flex items-start justify-between">
        <div>
          <Eyebrow>Human-in-the-loop</Eyebrow>
          <h1 className="mt-2 font-display text-3xl font-bold text-white">Needs Review</h1>
          <p className="mt-2 text-slate-400">Every AI draft lands here. Nothing commits to the record without a human decision.</p>
        </div>
        {canDelete && visible.length > 0 && (
          <button data-testid="review-clear-all-btn" onClick={clearAll}
            className="mt-2 inline-flex items-center gap-2 rounded-full border border-rose-500/50 bg-rose-950/40 px-4 py-2.5 text-sm text-rose-300 hover:bg-rose-900/50">
            <Trash2 className="h-4 w-4" /> Clear all
          </button>
        )}
      </div>

      {isLoading ? <div className="mt-10"><Spinner /></div> : (
        <div className="mt-8 space-y-4">
          {visible.map((d) => (
            <div key={d.id} className="card-tac p-5" data-testid={`review-item-${d.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="grid h-9 w-9 place-items-center rounded-lg bg-rose-500/10 text-rose-400"><Bot className="h-4.5 w-4.5" /></div>
                  <div>
                    <div className="text-sm font-medium text-white">{d.job_title || d.source}</div>
                    <div className="font-mono-tac text-[11px] uppercase tracking-widest text-slate-500">{d.source} · conf {Math.round(d.confidence * 100)}%</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {d.job_id && <Link to={`/jobs/${d.job_id}`} className="rounded-lg p-1.5 text-slate-500 hover:text-cyan-400"><ExternalLink className="h-4 w-4" /></Link>}
                  {canDelete && (
                    <button data-testid={`review-delete-${d.id}`} onClick={() => deleteOne(d)}
                      className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-rose-950/50 hover:text-rose-400" title="Delete draft">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <pre className="mt-4 overflow-x-auto rounded-xl border border-slate-800 bg-[#0d1420] p-3 text-xs text-slate-300" data-testid={`review-extracted-${d.id}`}>
{JSON.stringify(d.extracted, null, 2)}
              </pre>
              {canDecide && (
                <div className="mt-4 flex gap-3">
                  <button data-testid={`review-approve-${d.id}`} onClick={() => decide(d.id, "approve")} className="btn-amber inline-flex flex-1 items-center justify-center gap-2 rounded-xl py-2.5 text-sm"><Check className="h-4 w-4" /> Approve</button>
                  <button data-testid={`review-reject-${d.id}`} onClick={() => decide(d.id, "reject")} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-700 py-2.5 text-sm text-white hover:border-rose-500"><X className="h-4 w-4" /> Reject</button>
                </div>
              )}
            </div>
          ))}
          {!visible.length && <div className="card-tac py-16 text-center text-slate-500" data-testid="review-empty">Queue is clear. No drafts awaiting review.</div>}
        </div>
      )}
    </div>
  );
}
